import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import { nameof } from "../../../../../utils/nameof";
import { AssetInventoryCompletenessSection } from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { useMissingFieldMessages } from "../../../hooks/useMissingFieldMessages";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { describeMissingAssetField } from "../AssetsView.missingFields";
import { AssetsFormValues } from "../AssetsView.types";
import { ASSET_UPLOAD_AREAS, AssetUploadArea, fromAssetsResponse, toAssetsRequest } from "../AssetsView.utils";
import { createAssetsSchema } from "../AssetsView.validation";

export function useAssetAssets(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const moveDocuments = useAssetDocumentMoves(clientId);

  const queryKey = [ClientFileQueryKey.AssetInventoryAssets, clientId];

  const { data } = useQuery({
    queryKey,
    queryFn: () => clientsApi.getAssetInventoryAssets({ clientId }),
    enabled: !!clientId,
  });

  const uploadDocuments = async ({ area, queryKey: listQueryKey }: AssetUploadArea, files: File[]): Promise<File[]> => {
    if (!documentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await documentsApi.uploadAssetInventoryAssetDocument(
            { clientId, area },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [listQueryKey, clientId] });
    return failed;
  };

  const validationSchema = useMemo(() => createAssetsSchema(t), [t]);

  const initialValues = useMemo(() => fromAssetsResponse(data), [data]);

  const formik = useFormikWithDirtyForm<AssetsFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const request = toAssetsRequest(values);

      if (JSON.stringify(request) !== JSON.stringify(toAssetsRequest(initialValues))) {
        await clientsApi.saveAssetInventoryAssets({ clientId, saveAssetsRequest: request });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
        await queryClient.invalidateQueries({ queryKey });
        // A removed asset takes the securities that referred to it off the claims.
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClaimById] });
      }

      let uploadFailed = false;
      const hiddenZones = new Set<string>();
      for (const uploadArea of Object.values(ASSET_UPLOAD_AREAS)) {
        const pending = values[uploadArea.fieldName];
        if (!uploadArea.isShown(values.answers)) {
          hiddenZones.add(uploadArea.zone);
          if (pending.length > 0) {
            await helpers.setFieldValue(uploadArea.fieldName, []);
          }
          continue;
        }
        if (pending.length > 0) {
          const failed = await uploadDocuments(uploadArea, pending);
          await helpers.setFieldValue(uploadArea.fieldName, failed);
          uploadFailed ||= failed.length > 0;
        }
      }

      const moves = values.documentMoves.filter((move) => !hiddenZones.has(move.target));
      const failedMoves = await moveDocuments(moves);
      await helpers.setFieldValue(nameof<AssetsFormValues>("documentMoves"), failedMoves);

      if (!uploadFailed && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.assetsTab.saveSuccess"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  const missingFields = useMissingFieldMessages(
    clientId,
    AssetInventoryCompletenessSection.Assets,
    formik.values,
    describeMissingAssetField
  );

  return { formik, missingFields, onAddMove, onCancelMove };
}
